// rememberedUser.ts
// ───────────────────────────────────────────────────────────────────────────
// Qué recuerda el login en ESTE navegador (specs acceso/usuario-recordado).
//
// Solo se guarda el email, si este navegador activó la entrada rápida para ese
// email y cuándo se descartó la oferta de activarla. NUNCA una contraseña, un
// token, un código de 2FA ni datos de una credencial.
//
// El almacenamiento local puede estar bloqueado, vacío o lanzar (ventana
// privada, datos del sitio bloqueados): toda lectura y escritura va en
// try/catch y, si falla, el login se comporta como si no hubiera nada recordado.
// ───────────────────────────────────────────────────────────────────────────

const CLAVE = 'dd.quick-login.v1';

/** Cuánto no se vuelve a ofrecer activar la entrada rápida tras descartarla. */
export const PAUSA_DE_OFERTA_MS = 30 * 24 * 60 * 60 * 1000;

export interface RecuerdoLocal {
  email: string;
  /** Este navegador ya activó la entrada rápida para ese email. */
  rapido: boolean;
  /** Marca de tiempo (ms) en que se descartó la oferta de activarla. */
  ofertaDescartadaEn?: number;
}

type Almacen = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function almacenPorDefecto(): Almacen | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function normalizarEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function leerRecuerdo(almacen: Almacen | null = almacenPorDefecto()): RecuerdoLocal | null {
  if (!almacen) return null;
  try {
    const crudo = almacen.getItem(CLAVE);
    if (!crudo) return null;
    const r = JSON.parse(crudo) as Partial<RecuerdoLocal> | null;
    if (!r || typeof r.email !== 'string' || r.email.length === 0) return null;
    return {
      email: r.email,
      rapido: r.rapido === true,
      ofertaDescartadaEn: typeof r.ofertaDescartadaEn === 'number' ? r.ofertaDescartadaEn : undefined,
    };
  } catch {
    return null;
  }
}

function escribir(r: RecuerdoLocal, almacen: Almacen | null): void {
  if (!almacen) return;
  try {
    almacen.setItem(CLAVE, JSON.stringify(r));
  } catch {
    // sin almacenamiento: el login sigue andando sin recordar
  }
}

/**
 * Tras un ingreso exitoso. Si es el mismo email se conservan la marca de
 * entrada rápida y la pausa de la oferta; si es otro, se parte de cero (la
 * marca era de otra persona).
 */
export function recordarEmail(
  email: string,
  almacen: Almacen | null = almacenPorDefecto(),
): RecuerdoLocal {
  const limpio = normalizarEmail(email);
  const previo = leerRecuerdo(almacen);
  const r: RecuerdoLocal =
    previo && previo.email === limpio ? previo : { email: limpio, rapido: false };
  escribir(r, almacen);
  return r;
}

/** Este navegador activó la entrada rápida (alta de la passkey propia). */
export function activarRapido(
  email: string,
  almacen: Almacen | null = almacenPorDefecto(),
): RecuerdoLocal {
  const r: RecuerdoLocal = { email: normalizarEmail(email), rapido: true };
  escribir(r, almacen);
  return r;
}

/** La entrada rápida de este navegador dejó de servir (la passkey ya no está). */
export function desactivarRapido(almacen: Almacen | null = almacenPorDefecto()): RecuerdoLocal | null {
  const previo = leerRecuerdo(almacen);
  if (!previo) return null;
  const r: RecuerdoLocal = { email: previo.email, rapido: false, ofertaDescartadaEn: previo.ofertaDescartadaEn };
  escribir(r, almacen);
  return r;
}

export function descartarOferta(
  ahora: number = Date.now(),
  almacen: Almacen | null = almacenPorDefecto(),
): RecuerdoLocal | null {
  const previo = leerRecuerdo(almacen);
  if (!previo) return null;
  const r: RecuerdoLocal = { ...previo, ofertaDescartadaEn: ahora };
  escribir(r, almacen);
  return r;
}

/** «No soy yo»: olvida el email y la marca. No revoca nada en el servidor. */
export function olvidar(almacen: Almacen | null = almacenPorDefecto()): void {
  if (!almacen) return;
  try {
    almacen.removeItem(CLAVE);
  } catch {
    // nada que hacer
  }
}

export function ofertaPausada(r: RecuerdoLocal | null, ahora: number = Date.now()): boolean {
  return !!r?.ofertaDescartadaEn && ahora - r.ofertaDescartadaEn < PAUSA_DE_OFERTA_MS;
}

/**
 * ¿Corresponde ofrecer «Activar entrada rápida» tras este ingreso completo?
 * Sí cuando el navegador puede (tiene autenticador propio) y todavía no la tiene
 * para ESTE usuario, y no la descartó hace poco. Lo recordado de otra persona no
 * cuenta: ni su marca de entrada rápida ni su pausa.
 */
export function debeOfrecerActivacion(args: {
  recuerdo: RecuerdoLocal | null;
  emailQueEntro: string;
  hayAutenticador: boolean;
  ahora?: number;
}): boolean {
  if (!args.hayAutenticador) return false;
  const email = normalizarEmail(args.emailQueEntro);
  const mismo = args.recuerdo?.email === email ? args.recuerdo : null;
  if (mismo?.rapido) return false;
  return !ofertaPausada(mismo, args.ahora);
}
